import type { OptionSportsType } from '~/components/Projects/model';
import type { BreakpointsTypeKeys } from '~/const/appConst';
import type { LocaleCode } from '~/const/locale';
import type { AnswerType } from '~/model/AnswerType';
import type { AnswerChoicesRankingScaleType, AnswerChoicesRankingType, AnswerUserChoicesValueType } from '~/model/AnswerUserType';
import type { CriteriaChoiceType } from '~/model/CriteriaChoiceType';
import type { LanguageType, LocaleType } from '~/model/GlobalTypes';
import type { QuestionReconstructedType } from '~/model/QuestionType';
import type { SportType } from '~/model/SportType';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInHours from 'date-fns/differenceInHours';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import isDate from 'date-fns/isDate';
import decode from 'lean-he/decode';
import { animateScroll as scroll, scroller } from 'react-scroll';
import { acceptLanguages } from '@/i18n/routing';
import { apiTypes } from '~/const/api';
import { Breakpoints, breakpointsKeys, entityRoutes } from '~/const/appConst';
import layout from '~/const/layout';
import { ThemeType } from '~/model/GlobalTypes';

const isWindow = () => typeof window !== 'undefined';

// Prefered Color Scheme
export const isPreferedColorSchemeSupported = () => {
  if (!isWindow() || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia(layout.matchMedia.colorScheme).media !== 'not all';
  } catch {
    return false;
  }
};

const userPrefersDarkMode = () => {
  return (
    isPreferedColorSchemeSupported()
    && window.matchMedia?.(layout.matchMedia.dark)?.matches
  );
};

const userPrefersLightMode = () => {
  return (
    isPreferedColorSchemeSupported()
    && window.matchMedia?.(layout.matchMedia.light)?.matches
  );
};

export const getUserPreferedColorScheme = () => {
  if (!isPreferedColorSchemeSupported())
    return '';
  if (userPrefersDarkMode())
    return ThemeType.dark;
  if (userPrefersLightMode())
    return ThemeType.light;
  return '';
};

// Cookie
const isCookieAvailable = () => isWindow() && window.document.cookie;

export const getCookie = (cookieName: string) => {
  if (!isCookieAvailable())
    return;
  const cookie = {};
  window.document.cookie.split(';').forEach((el) => {
    const [key, value] = el.split('=');
    cookie[key.trim()] = value;
  });
  return cookie[cookieName];
};

export const removeCookie = (name: string) => {
  window.document.cookie = `${name}=; Path=/; Max-Age=-99999999;`;
};

export const setCookie = (value: string, age = 31536000) => {
  window.document.cookie = `${value};path=/;max-age=${age};`;
};

// Session storage
const isSessionStorageEnabled = () =>
  isWindow() && window.sessionStorage;

export const getSessionStorage = (key: string) => {
  return isSessionStorageEnabled() && window.sessionStorage.getItem(key);
};

export const setSessionStorage = (key: string, value: string) => {
  return isSessionStorageEnabled() && window.sessionStorage.setItem(key, value);
};

export const removeSessionStorage = (key: string) => {
  return isSessionStorageEnabled() && window.sessionStorage.removeItem(key);
};

const stripOutTags = (html = '', replacement = '') => {
  return (
    html
      // strip html tags
      .replace(/<.[^<>]*>/g, replacement)
      // replace less than and greater than characters
      // by corresponding html entities
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // convert 3 consecutive dots by ellipsis
      .replace(/\.\.\./g, '… ')
      // add whitespace after punctuation
      // not followed by whitespace
      .replace(/\.(?=\S)/g, '. ')
      .replace(/,(?=\S)/g, ', ')
      .replace(/:(?=\S)/g, ': ')
      .replace(/;(?=\S)/g, '; ')
  );
};

export const extractDescription = (html = '', length = 155) => {
  // replace every tag by a space
  const stripped = (html && stripOutTags(decode(html), ' ')) || '';
  const text = stripped
    // trim first space
    .replace(/^\S*\s/, '')
    // convert consecutive spaces into one
    .replace(/\s+/g, ' ');

  return text.length > length ? `${text.substring(0, length)}...` : text;
};

export const convertLinebreaks = (text: string) => {
  return text?.replace(/\r\n|\r|\n/g, '<br/>') || '';
};

export const convertLinebreaksForMail = (text: string) => {
  return text?.replace(/\r\n|\r|\n/g, '\n') || '';
};

export const ellipsis = (text = '', limit = 60) => {
  return text && text.length > limit ? `${text.substring(0, limit)}...` : text;
};

// get the days left until a deadline
export const getLeftDays = (deadline: string) => {
  let leftDays = null;
  if (deadline != null)
    leftDays = differenceInDays(new Date(deadline), new Date());

  return leftDays;
};

// get the hours left until a deadline
export const getLeftHours = (deadline: string) => {
  return deadline ? differenceInHours(new Date(deadline), new Date()) : null;
};

// get the minutes left until a deadline
export const getLeftMinutes = (deadline: string) => {
  let leftMinutes = null;
  if (deadline !== null)
    leftMinutes = differenceInMinutes(new Date(deadline), new Date());

  return leftMinutes;
};

// get the minutes left until a deadline
export const addLeadingZero = (minutes: number) => {
  let leadingZero = minutes || minutes === 0 ? `${minutes}` : null;
  if (
    minutes !== null
    && minutes !== undefined
    && minutes < 10
    && minutes >= 0
  ) {
    leadingZero = `0${minutes}`;
  }

  return leadingZero;
};

const formatIntegerValue = (value: string) => {
  // convert to integer if the value can be
  return (value || value === '0') && Number.isInteger(Number.parseInt(value, 10))
    ? +value
    : '';
};

const formatScaleValue = (value: number | string) => {
  // convert to float if the value can be
  if (typeof value === 'string' && !Number.isNaN(Number.parseFloat(`${value}`)))
    return Number.parseFloat(`${value}`);
  else if (!value && value !== 0)
    return null;

  return value;
};

const formatChoicesValue = (value: string | string[]) => {
  // convert the value to an array if it is not an array and not a date
  if (!Array.isArray(value) && !isDate(value)) {
    const arrayWrap: string[] = [];
    if (value)
      arrayWrap.push(value);

    return arrayWrap;
  }
  return value;
};

export const formatAnswerValue = (
  answerList: AnswerType[],
  answer: AnswerType,
  question: QuestionReconstructedType,
): {
  value?:
    | string
    | number
    | { choices: string[]; ranking_scale: string[] }
    | { choices: string[]; ranking: string[] };
  answer_choices?: string | string[] | Record<string, string | string[]>;
  media?: string;
} => {
  const formatedValue = {};

  switch (question.type) {
    case apiTypes.INTEGER:
      return { value: formatIntegerValue(`${answer.value_int}`) };
    case apiTypes.SCALE:
    case apiTypes.VAS1:
    case apiTypes.VAS2:
      return { value: formatScaleValue(answer.value_float) };
    case apiTypes.CHECK:
    case apiTypes.RADIO:
    case apiTypes.CONDITIONAL:
    case apiTypes.SELECT:
    case apiTypes.CHOICES_JAR:
      return {
        answer_choices: answer ? formatChoicesValue(answer.choices) : [],
        ...(answer?.value_text && { value_text: answer.value_text }),
      };
    case apiTypes.GRID:
      question.sub_questions.forEach((subquestion) => {
        const subAnswer = answerList?.find(
          previousAnswer => previousAnswer.sub_question === subquestion['@id'],
        );
        Object.assign(formatedValue, {
          [subquestion['@id']]: formatChoicesValue(subAnswer?.choices),
        });
      });
      return { answer_choices: formatedValue };
    case apiTypes.STAR_RATING:
      question.sub_questions.forEach((subquestion) => {
        const subAnswer = answerList?.find(
          previousAnswer => previousAnswer.sub_question === subquestion['@id'],
        );
        Object.assign(formatedValue, {
          [subquestion['@id']]: formatScaleValue(subAnswer?.value_float),
        });
      });
      return { answer_choices: formatedValue };
    case apiTypes.RANKING_PICTURE:
    case apiTypes.RANKING:
      return {
        answer_choices: { choices: answer.choices, ranking: answer.ranking },
      };
    case apiTypes.RANKING_SCALE:
      return {
        answer_choices: {
          choices: answer.choices,
          ranking_scale: answer.ranking_scale,
        },
      };
    case apiTypes.PICTURE:
    case apiTypes.FILE:
      return {
        value: answer.media['@id'],
        media: answer.media.content_url,
      };
    default:
      return { value: answer?.value_text || '' };
  }
};

export const getFormattedAnswerValue = (
  value: AnswerUserChoicesValueType,
  questionType: string,
) => {
  switch (questionType) {
    case apiTypes.INTEGER:
      return formatIntegerValue(value as string);
    case apiTypes.SCALE:
    case apiTypes.VAS1:
    case apiTypes.VAS2:
      return formatScaleValue(value as number);
    case apiTypes.CHECK:
    case apiTypes.RADIO:
    case apiTypes.CONDITIONAL:
    case apiTypes.SELECT:
    case apiTypes.GRID:
    case apiTypes.CHOICES_JAR:
      return formatChoicesValue(value as string | string[]);
    case apiTypes.RANKING_PICTURE:
    case apiTypes.RANKING:
      return {
        ...((value as AnswerChoicesRankingType).choices.length && {
          choices: (value as AnswerChoicesRankingType).choices,
        }),
        ...((value as AnswerChoicesRankingType).ranking.length && {
          ranking: (value as AnswerChoicesRankingType).ranking,
        }),
      };
    case apiTypes.RANKING_SCALE:
      return {
        ...{
          ...((value as AnswerChoicesRankingScaleType).choices.length && {
            choices: (value as AnswerChoicesRankingScaleType).choices,
          }),
          ...(Object.values(
            (value as AnswerChoicesRankingScaleType).ranking_scale,
          ).length && {
            ranking_scale: (value as AnswerChoicesRankingScaleType)
              .ranking_scale,
          }),
        },
      };
    default:
      return value;
  }
};

// image uploader
// check if the file is an image
export const checkImageUploadAuthorizedFormat = (file) => {
  if (
    file.type.includes('image/jpeg')
    || file.type.includes('image/jpg')
    || file.type.includes('image/png')
    || file.type.includes('image/gif')
    || file.type.includes('image/webp')
  ) {
    return true;
  }

  return false;
};

export const checkWebpConversion = (file: Blob) => {
  if (file?.type === 'image/webp')
    return true;
  return false;
};

export const checkFileUploadAuthorizedFormat = (file) => {
  if (
    file.type.includes('image/jpeg')
    || file.type.includes('image/jpg')
    || file.type.includes('image/png')
    || file.type.includes('image/gif')
    || file.type.includes('image/webp')
    || file.type.includes('application/pdf')
    || file.name.split('.').pop() === 'gpx'
  ) {
    return true;
  }

  return false;
};

export const fileSizeMegaBytesDefaultLimit = 10;

// check if the file is not too heavy
export const checkFileUploadAuthorizedSize = (
  file,
  limit: number = fileSizeMegaBytesDefaultLimit,
) => {
  return file.size < limit * 1000000;
};

export const getYoutubeIdFromUrl = (url) => {
  const regExp
    = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url?.match(regExp);

  if (match?.[2].length === 11)
    return match[2];

  return null;
};

export const getRouteFromType = (type: string) => {
  return entityRoutes[type.toLowerCase()];
};

export const reactScrollDefaultConfig = {
  duration: 400,
  offset: -80,
  smooth: true,
};

export const reactScrollConfigBumpy = {
  duration: 0,
  offset: 0,
  smooth: false,
};

export const scrollToTop = (
  config: Record<string, any> = reactScrollDefaultConfig,
) => {
  scroll.scrollToTop(config);
};

export const scrollToElement = (
  id: string,
  options: Record<string, any> = {},
) => {
  scroller.scrollTo(id, {
    ...reactScrollDefaultConfig,
    delay: 100,
    duration: 1000,
    ...options,
  });
};

export const capitalizeString = (value = '') =>
  value
    ? `${value?.[0].toUpperCase()}${value?.substring(1)?.toLowerCase()}`
    : '';

export const buildOptions = (list: Array<CriteriaChoiceType | SportType>) => {
  return list
    ? [...(list || [])].map((item) => {
        const label = capitalizeString(item.label);
        return {
          id: item.id,
          // As "label" can store a JSX value (favourite sports),
          label,
          // "title" serves as a plain text fallback
          title: label,
          value: item['@id'],
          ...((item as SportType).alias && {
            alias: (item as SportType).alias,
          }),
        };
      })
    : [];
};

export const roundToNDecimal = (number: number, nbDecimals = 1) => {
  const pow = 10 ** nbDecimals;
  return Math.round(number * pow) / pow;
};

type separatorType = '-' | '_';

export const convertLocaleSeparator = ({
  locale,
  separator = '_',
  newSeparator = '-',
}: {
  locale: LocaleType;
  separator?: separatorType;
  newSeparator?: separatorType;
}) => {
  return locale?.replace(separator, newSeparator) as LocaleType;
};

export const getLanguageFromLocale = (locale: LocaleType): LanguageType =>
  (locale?.length >= 2
    && (locale.includes('-') || locale.includes('_') || locale.length === 2)
    && locale?.split('-')[0].split('_')[0]) as LanguageType
    || undefined;

export const getLocaleFromLanguage = (
  language: LocaleCode,
  keepFormat = false,
) => {
  const locale = acceptLanguages.find(
    l => getLanguageFromLocale(l) === language,
  );
  return keepFormat ? locale : convertLocaleSeparator({ locale });
};

export const handleOptionSportsFilter = (
  sport: {
    label: string;
    value: string;
    data: OptionSportsType;
  },
  input: string,
) => {
  const normalizer = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036F]/g, '')
      .toLowerCase();

  const normalizedInput = normalizer(input);

  return (
    sport.data.alias?.some(alias =>
      normalizer(alias).includes(normalizedInput),
    ) || normalizer(sport.data.title).includes(normalizedInput)
  );
};

export const matchMediaQuery = (
  setBreakpoint: (value: BreakpointsTypeKeys) => void,
) => {
  for (const key of Object.keys(Breakpoints)) {
    if (window.matchMedia(`${Breakpoints[key]}`).matches)
      setBreakpoint(key as BreakpointsTypeKeys);
  }
};

export const handleSBreakpoint = (breakpoint: BreakpointsTypeKeys) => {
  return breakpoint === breakpointsKeys.get(Breakpoints.S);
};

export const handleMBreakpoint = (breakpoint: BreakpointsTypeKeys) => {
  return breakpoint === breakpointsKeys.get(Breakpoints.M);
};

export const handleLBreakpoint = (breakpoint: BreakpointsTypeKeys) => {
  return breakpoint === breakpointsKeys.get(Breakpoints.L);
};

export const handleXLBreakpoint = (breakpoint: BreakpointsTypeKeys) => {
  return breakpoint === breakpointsKeys.get(Breakpoints.XL);
};
